/* aa-cart-guard.js v3.1 - keeps the cutting charges attached to cut-to-size orders.
   A customer can delete a "Cutting Service Charges" line and still check out with
   material that carries cut instructions (free cutting). This reconciles per saw.

   v3 (30 Jul 2026) - THE GUARDED SET IS NOW A CONDITION, NOT A LIST.
   v2 guarded two hardcoded variant ids and looked for _cut_charge_qty /
   _cut_panel_qty / _cut_band_qty, which ONLY aa-plate.min.js writes. Every bar
   product - angle, channel, flat bar, RHS, round and square tube - stamps its
   charge line with _cutting_fee_for_product instead and declared nothing on the
   material line, so tally() computed "nothing required", found nothing to
   reconcile, and let the whole bar catalogue check out with no cutting charge.

   Now: a material line declares _cut_fee_qty (units owed) + _cut_fee_variant
   (which charge variant covers it), and the guarded set is BUILT FROM THE CART.
   Any saw, any rate, any charge variant added later is covered with no edit here.
   The two legacy plate properties still work, so carts already in flight are safe.

   v3.2 (1 Aug 2026) - ADDS THE POWDER-COAT SETUP FEE, as a separate pass.
   The $50 setup fee was enforced in the browser only, by assets/custom.js, which
   needs a colour -> variant map that is rendered inside the product form and is
   NOT in the DOM on /cart. So on the cart page custom.js early-returns and a
   deleted setup fee never comes back. The fee is once-per-colour, not per-unit, so
   it is NOT folded into tally() - tally sums, and summing $50 across two lines of
   one colour would double it. See setupFeeSteps().

   v3.3 (8 Sep 2026) - ZAPIET FIRST. The Checkout interception below used to submit
   the form itself once the charges reconciled. A programmatic submit carries no
   submitter, so name="checkout" never reached Shopify: with no pickup/delivery
   choice Zapiet had left the form action at /cart, the page simply re-rendered, and
   Zapiet's own "please select" words never showed because its click listener never
   ran. A customer rang: "I can't check out". Now, when Zapiet says checkout is not
   ready (window.ZapietCheckoutEnabled === false), the button says what is missing,
   the band scrolls into view, the date field is outlined, and the click is handed to
   Zapiet so its words show too. Nothing is submitted. Zapiet ready, or absent: the
   v3.2 path, unchanged. See zapietNudge / zapietHint.

   v3.4 (8 Sep 2026) - the Checkout button now lives at the end of the pickup / delivery
   step (sections/aa-cart-delivery-layout.liquid, layout B): a proxy #aaz-checkout whose
   click calls the real button's click(), with the real button visually hidden. So the
   v3.3 nudge lands under the proxy when there is one. One line; nothing else changed.

   v3.5 (8 Sep 2026) - THE BUTTON TELLS THE TRUTH. Patryk, after a day of layouts: "bring
   it back how it was but grey out the checkout? or when they press it to nudge them".
   Both. The cart section is back to July; this file makes the theme's own Checkout button
   read Zapiet's state (zapietState): while a method, a postcode or a date is missing it is
   grey and says which ("Choose pickup, delivery or elsewhere first" / "Enter your postcode
   first" / "Pick a date and time first") and a press scrolls to and opens that field -
   nothing is submitted; while the Elsewhere-in-Australia panel (aa-cart-quote) is open it
   is navy "Request a delivery quote" and a press goes to the quote page; when Zapiet says
   ready it is the normal "Checkout" and the v3.2 path runs, byte-identical. The v3.3 red
   note and grey hint are gone; one grey line sits under the button only while it is grey.
   No Zapiet on the page: nothing here runs. See zapietState / applyButton / goToMissing.

   v3.6 (8 Sep 2026) - when Zapiet says ready the button names the way: "Checkout - Store
   Pickup" / "Checkout - Delivery" (the theme's own word first, then the chosen method).

   v3.7 (26 Sep 2026) - THE SETUP FEE IS FREE ONCE A COLOUR REACHES THE COATER'S MINIMUM. Patryk's tick:
   "Free once Capral's minimum is reached" - about 12.5 m2 of coated surface per colour per order, as read
   from his 8 Sep quote. Area of a line = bars x bar length x the section's coated perimeter, from the
   product title and the variant length. Under the minimum the fee is owed exactly as before; at or over it
   setupFeeSteps() takes the colour out of `required` and removes its fee line. THIS FILE IS THE ONE PLACE
   THE RULE LIVES - custom.js, the bar tables and the made-to-order box ask AACartGuard.pcSetupWaived() etc.

   Runs on cart load, after cart AJAX changes, and as a hard check on Checkout.
   ASCII only. Config via window.AA_CART_GUARD_CFG. */
(function () {
  'use strict';
  var CFG = (typeof window !== 'undefined' && window.AA_CART_GUARD_CFG) ? window.AA_CART_GUARD_CFG : {};
  var CUT_VARIANT = Number(CFG.cutVariantId || 42855746568269);   // Panel Saw Cut / 5 (also legacy consolidated line)
  var BAND_VARIANT = Number(CFG.bandVariantId || 42855746175053); // Band Saw Cut / 5

  // Legacy declarations: property -> the fixed variant it always meant.
  // "Legacy" names the property shape, NOT the dependency: assets/aa-plate.min.js
  // is the LIVE plate engine and writes these today, so this branch is the only
  // thing guarding plate cutting. Deleting it in a tidy-up would silently unguard
  // every plate cut - r185 again, through the plate door. It also keeps in-flight
  // plate carts reconciling. Keep it.
  var LEGACY = [
    { prop: '_cut_charge_qty', id: CUT_VARIANT, label: 'Panel-saw cutting' },
    { prop: '_cut_panel_qty', id: CUT_VARIANT, label: 'Panel-saw cutting' },
    { prop: '_cut_band_qty', id: BAND_VARIANT, label: 'Bandsaw cutting & handling' }
  ];
  // The variant-aware declaration. Written on the MATERIAL line by the collection
  // tables and the product-page cut modal.
  var FEE_QTY = '_cut_fee_qty';
  var FEE_VAR = '_cut_fee_variant';
  // A line IS a cutting charge line if it carries one of these. Charge lines only
  // ever carry them, and material lines never do - so this identifies a charge
  // line without knowing any variant id in advance.
  var CHARGE_MARKS = ['_cutting_fee_for_product', '_cutting_for'];
  // Human labels per saw, for the line we add back.
  var METHOD_LABEL = {
    'up-saw': 'Up-saw cutting',
    'band-saw': 'Bandsaw cutting & handling',
    'panel-saw': 'Panel-saw cutting',
    'rip-cut': 'Rip cutting',
    'oversized-cut': 'Oversized cutting',
    'folding-prep-cut': 'Folding prep cutting'
  };

  var busy = false;
  var reloadScheduled = false;
  function onCartPage() { return /\/cart(\/|$|\?)/.test(location.pathname); }
  function reloadedRecently() { try { return (Date.now() - (+sessionStorage.getItem('aaCartGuardReloadTs') || 0)) < 6000; } catch (e) { return false; } }
  function markReloaded() { try { sessionStorage.setItem('aaCartGuardReloadTs', String(Date.now())); } catch (e) {} }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }
  /* A Shopify cart error RESOLVES - 422 with { status, message, description } - so
     .then(r => r.json()) alone cannot tell a refusal from a success, and every repair
     step in this file routes through here. Without this check a refused add is treated
     as done: the fee latch is released only on a REJECTION (see setupFeeSteps), and
     announce() tells the customer a charge was added back that was not.
     r.ok is the discriminator and is the half to trust. The numeric j.status is
     belt-and-braces for anything between us and Shopify that rewrites a status code -
     safe only because neither success body carries a numeric status.
     Throwing here also SHORT-CIRCUITS the chain, which is deliberate: a partially
     applied repair leaves the cart worse than an abandoned one, and the next
     reconcile retries anyway. Checkout stays blocked either way - anyChange is
     computed before any step runs, so reconcile() already returns true. */
  function req(url, body) {
    return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok || (j && Number(j.status) >= 400)) {
            var err = new Error('cart request failed: ' + (r.status || (j && j.status) || '?'));
            err.cartError = j;
            throw err;
          }
          return j;
        });
      });
  }
  function rawProp(item, key) {
    return (item && item.properties) ? item.properties[key] : null;
  }
  function intProp(item, key) {
    var n = parseInt(rawProp(item, key), 10);
    return isNaN(n) ? 0 : n;
  }
  function hasProp(item, key) {
    var v = rawProp(item, key);
    return v !== null && v !== undefined && String(v) !== '';
  }

  /* Per charge variant: how many units the cart's cut lines REQUIRE, and how many
     are actually present. Neither side is a hardcoded list.

     required - summed from declarations on material lines (legacy props, or
                _cut_fee_qty + _cut_fee_variant).
     present  - summed from lines identified as charge lines by CHARGE_MARKS, plus
                the two legacy variants which are always reconciled so a plate
                charge left behind by a deleted plate line is still trimmed.

     Safety rule: a charge variant we only DISCOVERED (never declared for, and not
     one of the two legacy ids) is left completely alone unless the cart still
     contains the product it was stamped for. That way an older cart, built before
     this version shipped, is never stripped of a charge it legitimately owes. */
  function tally(cart) {
    var items = cart.items || [];
    var need = {}, lines = {}, label = {}, covered = {}, declared = {};
    var forPid = {}, forMethod = {};
    var productIdsInCart = {};

    items.forEach(function (it) {
      if (it.product_id != null) productIdsInCart[String(it.product_id)] = true;
    });

    function want(vid, units, title, lbl, pid, method) {
      vid = Number(vid);
      if (!vid || !(units > 0)) return;
      need[vid] = (need[vid] || 0) + units;
      declared[vid] = true;
      if (lbl && !label[vid]) label[vid] = lbl;
      if (pid && !forPid[vid]) forPid[vid] = String(pid);
      if (method && !forMethod[vid]) forMethod[vid] = String(method);
      if (title) {
        covered[vid] = covered[vid] || [];
        if (covered[vid].indexOf(title) < 0) covered[vid].push(title);
      }
    }

    // 1. What the cart requires - declared on the material lines.
    items.forEach(function (it) {
      var i;
      var method = String(rawProp(it, '_cut_method') || '');
      for (i = 0; i < LEGACY.length; i++) {
        want(LEGACY[i].id, intProp(it, LEGACY[i].prop), it.product_title, LEGACY[i].label, it.product_id, method || 'panel-saw');
      }
      var v = Number(rawProp(it, FEE_VAR));
      if (v) {
        want(v, intProp(it, FEE_QTY), it.product_title, METHOD_LABEL[method] || 'Cutting', it.product_id, method || 'up-saw');
      }
    });

    // 2. What the cart actually holds - charge lines identified by their markers.
    items.forEach(function (it) {
      var vid = Number(it.variant_id), i, isCharge = false, stampedFor;
      if (vid === CUT_VARIANT || vid === BAND_VARIANT) isCharge = true;
      if (!isCharge) {
        for (i = 0; i < CHARGE_MARKS.length; i++) { if (hasProp(it, CHARGE_MARKS[i])) { isCharge = true; break; } }
      }
      if (!isCharge) return;

      // Discovered-only variant: reconcile it only when we can prove the job it
      // belongs to is gone, or something in this cart declares for it.
      if (!declared[vid] && vid !== CUT_VARIANT && vid !== BAND_VARIANT) {
        stampedFor = rawProp(it, '_cutting_fee_for_product');
        if (!stampedFor || productIdsInCart[String(stampedFor)]) return;  // job still present, or unknowable - leave it
      }

      lines[vid] = lines[vid] || [];
      lines[vid].push({ key: it.key, qty: Number(it.quantity) || 0 });
      if (!label[vid]) label[vid] = METHOD_LABEL[String(rawProp(it, '_cut_method') || '')] || 'Cutting';
    });

    // 3. Everything either side knows about.
    var ids = {}, k;
    ids[CUT_VARIANT] = true;
    ids[BAND_VARIANT] = true;
    for (k in need) if (need.hasOwnProperty(k)) ids[k] = true;
    for (k in lines) if (lines.hasOwnProperty(k)) ids[k] = true;

    var entries = [], allCovered = [];
    Object.keys(ids).forEach(function (key) {
      var vid = Number(key);
      var ls = lines[vid] || [];
      var present = 0;
      ls.forEach(function (l) { present += l.qty; });
      entries.push({
        id: vid,
        required: need[vid] || 0,
        present: present,
        lines: ls,
        label: label[vid] || 'Cutting',
        forPid: forPid[vid] || '',
        method: forMethod[vid] || ''
      });
      (covered[vid] || []).forEach(function (t) { if (allCovered.indexOf(t) < 0) allCovered.push(t); });
    });
    return { entries: entries, covered: allCovered };
  }

  /* ---- POWDER-COAT SETUP FEE ------------------------------------------------
     Separate from tally() on purpose. tally() SUMS units: a cut line owing 6 cuts
     and another owing 3 means 9 charge units. A setup fee is charged ONCE PER
     COLOUR PER ORDER (the product page says so in as many words), so summing it
     would bill $100 for two Black lines. This pass is PRESENCE-CAPPED instead:
     each declared variant must appear exactly once.

     What declares it: a MATERIAL line carrying _setup_fee_variant (which fee
     variant covers it) + _setup_fee_colour (the label). Written at add time by
     assets/custom.js and, once Lane B regenerates them, the four bar tables.

     ADD AND CAP ONLY. It never removes a fee line it holds no declaration for.
     Right now most powder carts carry no declaration at all, and "undeclared" must
     never be read as "orphaned" - trimming those would delete $50 the customer
     genuinely owes, which is the v2 mistake in the other direction. Orphan removal
     stays where it already works: custom.js, on a product page, where the map is. */
  var SETUP_VAR = '_setup_fee_variant';
  var SETUP_COL = '_setup_fee_colour';
  var SETUP_MARK = '_setup_fee_for_colour';
  var SETUP_MARK_LEGACY = '_powder_fee_for_colour';
  // Variants we have already re-added once in this page's life. If a fee we added
  // goes missing again, something else in the page is removing it (custom.js can
  // decide a colour is absent when the material line spells it differently) - so we
  // stand down rather than ping-pong with it for the rest of the session.
  var setupAdded = {};

  function isSetupFeeLine(it) {
    return hasProp(it, SETUP_MARK) || hasProp(it, SETUP_MARK_LEGACY) || hasProp(it, 'Setup Fee');
  }

  function normColour(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  // Every colour string a remaining MATERIAL line offers: its declaration, its
  // Colour property, and its variant title (the colour often lives only there).
  function pushWanted(list, it) {
    [rawProp(it, SETUP_COL), rawProp(it, 'Colour'), it && it.variant_title].forEach(function (v) {
      var c = normColour(v);
      if (c && list.indexOf(c) < 0) list.push(c);
    });
  }
  // Deliberately generous, in both directions: "Black" on a material line holds a
  // "Black Powder Coat" fee. Matching too widely only ever KEEPS a charge, which is
  // the safe way to be wrong.
  function stillWanted(colour, wanted) {
    for (var i = 0; i < wanted.length; i++) {
      if (wanted[i] === colour || wanted[i].indexOf(colour) >= 0 || colour.indexOf(wanted[i]) >= 0) return true;
    }
    return false;
  }

  function setupProps(colour) {
    var c = String(colour || '').trim();
    var p = { 'Setup Fee': 'Powder Coating' };
    p[SETUP_MARK] = c;
    p[SETUP_MARK_LEGACY] = c;
    if (c) p['Colour'] = c;
    return p;
  }

  /* v3.7 - THE COATER'S MINIMUM. The setup fee is owed only while a custom colour's order is under
     PC_FREE_M2 of coated surface. A line whose section or length cannot be read counts as 0 m2, so the fee
     stays - the safe way to be wrong. Coated perimeter per metre, from the title's sizes in order:
     angle A x B x T = 2(A+B) mm; channel A x B x T (A the web, B the legs) = 4B + 2A - 2T mm.
     Checked against the 8 Sep quote: 25x12x3 angle, 0.074 m2/m x 6.5 m = 0.481 m2 a length, and
     12.5 / 0.481 = 26 lengths - the MOQ the coater gave. */
  var PC_FREE_M2 = Number(CFG.pcSetupFreeM2 || 12.5);
  function pcPerimeterM(title) {
    var t = String(title || '');
    var fam = /\bangle\b/i.test(t) ? 'angle' : (/\bchannel\b/i.test(t) ? 'channel' : '');
    if (!fam) return 0;
    var n = [], re = /(\d+(?:\.\d+)?)\s*mm\b/gi, m;
    while ((m = re.exec(t)) !== null) n.push(parseFloat(m[1]));
    if (n.length < 3 || !(n[0] > 0 && n[1] > 0 && n[2] > 0)) return 0;
    return fam === 'angle' ? 2 * (n[0] + n[1]) / 1000 : (4 * n[1] + 2 * n[0] - 2 * n[2]) / 1000;
  }
  // "6.5m - Full Length / Monument Powder Coat" -> 6.5. Millimetre lengths ("500mm") do not match.
  function pcLengthM(text) {
    var m = String(text || '').match(/(\d+(?:\.\d+)?)\s*m\b/i);
    return m ? parseFloat(m[1]) : 0;
  }
  function pcAreaM2(title, lengthM, qty) {
    var per = pcPerimeterM(title), len = Number(lengthM) || 0, q = Number(qty) || 0;
    return (per > 0 && len > 0 && q > 0) ? per * len * q : 0;
  }
  // The coated area a colour's MATERIAL lines already hold - only lines declaring that fee variant count.
  function pcColourAreaM2(cart, feeVid) {
    var total = 0;
    ((cart && cart.items) || []).forEach(function (it) {
      if (isSetupFeeLine(it) || Number(rawProp(it, SETUP_VAR)) !== Number(feeVid)) return;
      total += pcAreaM2(it.product_title, pcLengthM(it.variant_title), it.quantity);
    });
    return total;
  }
  // extraM2: an add not yet in the cart (the add paths ask before they add).
  function pcSetupWaived(cart, feeVid, extraM2) {
    var a = pcColourAreaM2(cart, feeVid) + (Number(extraM2) || 0);
    return Math.round(a * 1000) / 1000 >= PC_FREE_M2;
  }
  // How many lengths of ONE size reach the minimum - for the words on the page. 0 = cannot tell.
  function pcFreeFromLengths(title, lengthM) {
    var a = pcAreaM2(title, lengthM || 6.5, 1);
    return a > 0 ? Math.ceil(Math.round(PC_FREE_M2 / a * 1000) / 1000) : 0;
  }

  function setupFeeSteps(cart) {
    var items = cart.items || [];
    var required = {}, lines = {}, wanted = [];
    var out = { steps: [], topUp: false, changed: false };

    // 1. What the cart requires - declared on material lines only.
    items.forEach(function (it) {
      if (isSetupFeeLine(it)) return;               // a fee line cannot declare for itself
      pushWanted(wanted, it);                       // ...but every material line votes on colour
      var vid = Number(rawProp(it, SETUP_VAR));
      if (!vid) return;
      if (!(vid in required)) required[vid] = String(rawProp(it, SETUP_COL) || '');
    });

    /* 1b. v3.7 - a colour at or over the coater's minimum owes no setup fee. Out of `required`, and its fee
       line(s) removed. Its declarations stay on the material lines, so if the order later drops back under
       the minimum the colour is required again - and the add latch is released so the fee CAN come back. */
    var waived = {};
    Object.keys(required).forEach(function (key) {
      if (pcSetupWaived(cart, Number(key))) { waived[key] = required[key]; delete required[key]; }
    });
    items.forEach(function (it) {
      var vid = Number(it.variant_id);
      if (!vid || !(vid in waived)) return;
      delete setupAdded[vid];
      out.changed = true;
      out.steps.push((function (k) { return function () { return req('/cart/change.js', { id: k, quantity: 0 }); }; })(it.key));
    });
    Object.keys(waived).forEach(function (key) { delete setupAdded[Number(key)]; });

    // 2. What the cart holds, for those variants only.
    items.forEach(function (it) {
      var vid = Number(it.variant_id);
      if (!vid || !(vid in required)) return;
      lines[vid] = lines[vid] || [];
      lines[vid].push({ key: it.key, qty: Number(it.quantity) || 0 });
    });

    /* 2b. ORPHAN SWEEP - the removal half this pass never had.
       Mirrors tally()'s "prove the job is gone" rule, with colour standing in for
       product id. Only fee lines whose variant is NOT in `required` are looked at,
       so this can never race the add/cap loop below (the sets are disjoint by
       construction, hence no double-count). It removes only when the cart itself
       proves the colour is gone: an unreadable colour, or any material line that
       still names it however loosely, keeps the fee - so undeclared legacy powder
       carts stay safe. quantity 0 deletes the exact line; no amount is recomputed,
       and topUp is left false so the "added back" banner never fires on a removal. */
    items.forEach(function (it) {
      var vid = Number(it.variant_id);
      if (!isSetupFeeLine(it) || (vid in required) || (vid in waived)) return;   // v3.7: waived fees are removed above
      var c = normColour(rawProp(it, SETUP_MARK) || rawProp(it, SETUP_MARK_LEGACY) || rawProp(it, 'Colour'));
      if (!c || stillWanted(c, wanted)) return;     // unknowable, or still wanted - leave it
      out.changed = true;
      out.steps.push((function (k) { return function () { return req('/cart/change.js', { id: k, quantity: 0 }); }; })(it.key));
    });

    Object.keys(required).forEach(function (key) {
      var vid = Number(key);
      var ls = lines[vid] || [];
      var total = 0, i;
      ls.forEach(function (l) { total += l.qty; });

      if (ls.length === 0) {
        if (setupAdded[vid]) return;
        setupAdded[vid] = true;
        out.changed = true;
        out.topUp = true;
        out.steps.push((function (id, colour) { return function () {
          /* The claim above is provisional: it stops this same pass queueing two adds.
             If the add FAILS the claim is released so the next reconcile retries -
             otherwise one network blip marks the fee handled for the life of the
             page and the customer checks out without it. Re-throw, so the chain's
             catch still blocks checkout and announce() never claims a fee was
             added back that was not. */
          return req('/cart/add.js', { items: [{ id: id, quantity: 1, properties: setupProps(colour) }] })
            .catch(function (err) { delete setupAdded[id]; throw err; });
        }; })(vid, required[vid]));
        return;
      }

      if (total !== 1 || ls.length > 1) {           // once per colour, never more
        out.changed = true;
        out.steps.push((function (k) { return function () { return req('/cart/change.js', { id: k, quantity: 1 }); }; })(ls[0].key));
        for (i = 1; i < ls.length; i++) {
          out.steps.push((function (k) { return function () { return req('/cart/change.js', { id: k, quantity: 0 }); }; })(ls[i].key));
        }
      }
    });

    return out;
  }

  // Make each cutting-charge total EXACTLY what the cut lines require. Tops up or
  // adds when short (customer removed/reduced the charge), and trims or removes
  // when in excess (customer removed a material line, e.g. to edit its sizes) so no
  // orphaned charge is ever left behind. Resolves true if it changed anything.
  function reconcile() {
    if (busy) return Promise.resolve(false);
    busy = true;
    return getCart().then(function (cart) {
      var t = tally(cart);
      var steps = [], anyTopUp = false, anyChange = false;
      var forTxt = t.covered.length ? (t.covered.slice(0, 3).join(', ') + (t.covered.length > 3 ? ' + ' + (t.covered.length - 3) + ' more' : '')) : 'cut-to-size order';
      t.entries.forEach(function (e) {
        if (e.present === e.required) return;
        anyChange = true;
        if (e.present < e.required) anyTopUp = true;
        if (e.lines.length === 0) {
          if (e.required > 0) steps.push((function (entry, need) { return function () {
            /* Stamp it exactly like the line the tool would have added, so the
               cart renders it identically (no Remove link, right saw name) and
               the OS parsers see what they expect. */
            /* No dollar amount in this string. The guard knows how many units
               are owed but not what that variant charges per unit - CUT_FEE was
               the plate rate and would print "$35" next to a $7.00 line on any
               bar product. The line's own price is already shown; a number that
               disagrees with it is worse than no number. */
            var addProps = { '_cutting_for': 'cut-to-size order', 'For': forTxt, 'Cutting': entry.label };
            if (entry.forPid) addProps['_cutting_fee_for_product'] = entry.forPid;
            if (entry.method) addProps['_cut_method'] = entry.method;
            return req('/cart/add.js', { items: [{ id: entry.id, quantity: need, properties: addProps }] });
          }; })(e, e.required));
        } else {
          // first charge line carries the whole requirement (0 removes it); zero any extras
          steps.push((function (k, need) { return function () { return req('/cart/change.js', { id: k, quantity: need }); }; })(e.lines[0].key, e.required));
          for (var i = 1; i < e.lines.length; i++) {
            steps.push((function (k) { return function () { return req('/cart/change.js', { id: k, quantity: 0 }); }; })(e.lines[i].key));
          }
        }
      });
      // Powder-coat setup fee, on the same chain and the same call sites.
      var sf = setupFeeSteps(cart);
      if (sf.changed) { anyChange = true; steps = steps.concat(sf.steps); }
      if (!anyChange) { busy = false; return false; }
      var chain = Promise.resolve();
      steps.forEach(function (s) { chain = chain.then(s); });
      return chain.then(function () { busy = false; announce(anyTopUp, sf.topUp); return true; })
        .catch(function () { busy = false; return true; });
    }).catch(function () { busy = false; return false; });
  }

  // Fixed literals only - never interpolate cart content into these.
  var MSG_CUT = '<strong>Cutting charge required.</strong> A cutting charge applies to your cut-to-size items, so it has been added back to your cart.';
  var MSG_SETUP = '<strong>Setup fee required.</strong> A one-off powder coat setup fee applies per colour (free on larger orders of that colour), so it has been added back to your cart.';
  var MSG_BOTH = '<strong>Charges required.</strong> The cutting charge and the powder coat setup fee both apply to this order, so they have been added back to your cart.';
  function msgFor(kind) {
    if (kind === 'both') return MSG_BOTH;
    if (kind === 'setup') return MSG_SETUP;
    return MSG_CUT;                                  // also the fallback for the old '1' flag
  }

  function notify(msg) {
    try {
      var id = 'aa-cart-guard-note';
      if (document.getElementById(id)) return;
      var d = document.createElement('div');
      d.id = id;
      d.innerHTML = msg || MSG_CUT;
      d.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:2147483600;background:#b3261e;color:#fff;padding:13px 46px 13px 18px;font:14px/1.45 -apple-system,Segoe UI,Roboto,sans-serif;text-align:center;box-shadow:0 3px 14px rgba(0,0,0,.22);';
      var x = document.createElement('button');
      x.type = 'button'; x.textContent = 'x'; x.setAttribute('aria-label', 'Dismiss');
      x.style.cssText = 'position:absolute;right:14px;top:7px;background:transparent;border:0;color:#fff;font-size:20px;line-height:1;cursor:pointer;';
      x.onclick = function () { if (d.parentNode) d.parentNode.removeChild(d); };
      d.appendChild(x);
      document.body.appendChild(d);
      setTimeout(function () { if (d.parentNode) d.parentNode.removeChild(d); }, 9000);
    } catch (e) {}
  }

  // Show the notice, then refresh the UI. On the cart PAGE we must reload for the
  // re-added line to appear - but a reload wipes the toast, so we stash a flag and
  // re-show it after the reload. On other pages (drawer) just toast + nudge events.
  function announce(cutTopUp, setupTopUp) {
    var showBanner = !!(cutTopUp || setupTopUp);
    var kind = (cutTopUp && setupTopUp) ? 'both' : (setupTopUp ? 'setup' : 'cut');
    if (onCartPage()) {
      // Already reloaded once for this fix -> don't loop; just show the banner now.
      if (reloadedRecently()) { if (showBanner) notify(msgFor(kind)); return; }
      if (reloadScheduled) return;
      reloadScheduled = true;
      markReloaded();
      if (showBanner) { try { sessionStorage.setItem('aaCartGuardNote', kind); } catch (e) {} }
      setTimeout(function () { location.reload(); }, 350);
      return;
    }
    if (showBanner) notify(msgFor(kind));
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
      if (window.jQuery) window.jQuery(document).trigger('cart:refresh');
    } catch (e) {}
  }

  // Hard backstop: intercept Checkout so a cart can't leave without its cutting charge.
  function isCheckoutTrigger(el) {
    if (!el) return false;
    if (el.name === 'checkout') return true;
    // Standard checkout only - do NOT intercept express buttons (Shop Pay / Apple
    // Pay); those use the live cart, which load/AJAX reconcile already keeps correct.
    if (el.matches && el.matches('[name="checkout"],[href$="/checkout"],[href*="/checkout?"],.cart__checkout')) return true;
    return false;
  }
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('button,a,input') : null;
    if (!isCheckoutTrigger(el)) return;
    if (el.getAttribute && el.getAttribute('data-aa-guard-ok') === '1') return;   // already verified this click
    e.preventDefault(); e.stopImmediatePropagation();
    // v3.5: the button says what is missing; a press on a grey button goes there, on a navy one to the quote page.
    var zs = zapietState();
    if (zs.present && zs.next === 'quote') { location.href = zs.quoteUrl; return; }
    if (zs.present && zs.next !== 'ready') { goToMissing(zs); return; }
    reconcile().then(function (fixed) {
      if (fixed) { return; }   // charge was re-added; user stays on cart to review (notice shown)
      // clean - let the original action through
      el.setAttribute('data-aa-guard-ok', '1');
      setTimeout(function () {
        el.removeAttribute('data-aa-guard-ok');
      }, 4000);
      if (el.tagName === 'A' && el.href) { location.href = el.href; }
      else if (el.form) { el.form.submit(); }
      else { location.href = '/checkout'; }
    });
  }, true);

  /* ---- v3.5 ZAPIET: THE BUTTON TELLS THE TRUTH --------------------------------
     Zapiet (Pickup + Delivery) keeps window.ZapietCheckoutEnabled false until a method
     and a date are chosen, and fires 'checkoutEnabled' / 'checkoutDisabled' on
     window.ZapietEvent when that changes. What is read off its widget (#storePickupApp):
     the checked method, whether its date field is on screen and filled, the delivery
     postcode field, and aa-cart-quote's Elsewhere card / panel. Nothing else is assumed.
     If Zapiet is not on the page none of this runs and the button is the theme's own. */
  var HINT_ID = 'aa-checkout-hint', STYLE_ID = 'aa-checkout-style';
  var WORDS = {
    method: 'Choose pickup, delivery or elsewhere first',
    postcode: 'Enter your postcode first',
    date: 'Pick a date and time first',
    quote: 'Request a delivery quote'
  };
  var HINTS = {
    method: 'Pick Store Pickup, Delivery or Elsewhere in Australia below.',
    postcode: 'Press it and it takes you to the postcode field.',
    date: 'Press it and it takes you to the date field.',
    quote: 'We quote freight for the rest of Australia - your cart goes with the request.'
  };
  function zapietApp() { return document.getElementById('storePickupApp'); }
  function zapietState() {
    var a = zapietApp();
    var st = { present: !!(a || window.Zapiet), method: '', dateField: false, dateText: '', ready: (window.ZapietCheckoutEnabled === true), elsewhere: false, quoteUrl: '/pages/delivery-quote', next: 'ready' };
    if (!a) { if (st.present) { st.next = st.ready ? 'ready' : 'method'; } return st; }
    try {
      var lab = a.querySelector('.checkoutMethodsContainer label input:checked');
      lab = lab ? lab.closest('label') : null;
      var mt = lab ? String(lab.textContent || '').replace(/\s+/g, ' ').trim() : '';
      st.method = /pick\s*up/i.test(mt) ? 'pickup' : (mt ? 'delivery' : '');
      var f = a.querySelector('.Zapiet-InputWithPrefix__Input');
      st.dateField = !!f; st.dateText = f ? String(f.value || '').trim() : '';
      var card = a.querySelector('.aaq-au'), panel = a.querySelector('.aaq-oos');
      st.elsewhere = !!(panel || (card && card.getAttribute('aria-expanded') === 'true'));
      var pl = panel ? panel.querySelector('a[href]') : null;
      if (pl && pl.getAttribute('href')) st.quoteUrl = pl.getAttribute('href');
    } catch (e) {}
    if (st.elsewhere) st.next = 'quote';
    else if (!st.method) st.next = 'method';
    else if (st.ready && st.dateText) st.next = 'ready';
    else if (st.method === 'delivery' && !st.dateField) st.next = 'postcode';
    else st.next = 'date';
    return st;
  }
  function checkoutButton() {
    return document.querySelector('form.cart-recap [name="checkout"], .cart-recap__checkout, form[action="/cart"] [name="checkout"]');
  }
  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent = '.cart-recap__checkout.aa-wait{background:#e5e7eb !important;border-color:#e5e7eb !important;color:#6b7280 !important;box-shadow:none !important;}'
      + '.cart-recap__checkout.aa-wait:hover{background:#d1d5db !important;border-color:#d1d5db !important;color:#0e1a4d !important;}'
      + '.cart-recap__checkout.aa-quote{background:#0e1a4d !important;border-color:#0e1a4d !important;color:#fff !important;}'
      + '#' + HINT_ID + '{margin:8px 0 0;font-size:12.5px;line-height:1.4;color:#64748b;text-align:center;}'
      + '.aa-flash{box-shadow:0 0 0 5px rgba(18,184,229,.45) !important;transition:box-shadow .2s;}'
      + '.checkoutMethodsContainer.aa-flash{box-shadow:none !important;outline:3px solid #12b8e5;outline-offset:6px;border-radius:12px;}';
    document.head.appendChild(st);
  }
  function applyButton() {
    var btn = checkoutButton(); if (!btn) return;
    var zs = zapietState();
    var hint = document.getElementById(HINT_ID);
    if (!zs.present) {   // no Zapiet: the theme's own button, untouched
      if (btn.getAttribute('data-aa-orig') != null) { btn.textContent = btn.getAttribute('data-aa-orig'); btn.removeAttribute('data-aa-orig'); }
      btn.classList.remove('aa-wait'); btn.classList.remove('aa-quote');
      if (hint && hint.parentNode) hint.parentNode.removeChild(hint);
      return;
    }
    ensureStyle();
    if (btn.getAttribute('data-aa-orig') == null) btn.setAttribute('data-aa-orig', btn.textContent);
    var want = (zs.next === 'ready') ? (btn.getAttribute('data-aa-orig') + (zs.method === 'pickup' ? ' \u2014 Store Pickup' : (zs.method === 'delivery' ? ' \u2014 Delivery' : ''))) : WORDS[zs.next];   // v3.6
    if (btn.textContent !== want) btn.textContent = want;
    btn.classList.toggle('aa-wait', zs.next === 'method' || zs.next === 'postcode' || zs.next === 'date');
    btn.classList.toggle('aa-quote', zs.next === 'quote');
    btn.setAttribute('data-aa-next', zs.next);
    if (zs.next === 'ready') {
      if (hint && hint.parentNode) hint.parentNode.removeChild(hint);
      return;
    }
    if (!hint) {
      hint = document.createElement('div');
      hint.id = HINT_ID;
    }
    if (hint.textContent !== HINTS[zs.next]) hint.textContent = HINTS[zs.next];
    if (btn.nextElementSibling !== hint) btn.insertAdjacentElement('afterend', hint);   // always right under the button
  }
  function flash(el, focus) {
    if (!el) return;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { try { el.scrollIntoView(); } catch (e2) {} }
    el.classList.add('aa-flash');
    setTimeout(function () { try { if (focus) { el.focus(); el.click(); } } catch (e) {} }, 350);   // Zapiet opens its calendar on the field
    setTimeout(function () { el.classList.remove('aa-flash'); }, 3500);
  }
  function goToMissing(zs) {
    var a = zapietApp(); if (!a) return;
    if (zs.next === 'method') { flash(a.querySelector('.checkoutMethodsContainer') || a, false); return; }
    if (zs.next === 'postcode') { flash(a.querySelector('.checkoutMethodContainer.delivery input:not([type="radio"]):not([type="checkbox"])') || a, true); return; }
    var f = a.querySelector('.Zapiet-InputWithPrefix__Input');
    flash(f || a, !!f);
  }
  function zapietWatch() {
    if (!onCartPage()) return;
    var bound = false;
    var bind = function () {
      if (bound || !window.ZapietEvent || typeof window.ZapietEvent.listen !== 'function') return;
      bound = true;
      try { window.ZapietEvent.listen('checkoutEnabled', applyButton); window.ZapietEvent.listen('checkoutDisabled', applyButton); } catch (e) {}
    };
    bind(); applyButton();
    setInterval(function () { bind(); try { applyButton(); } catch (e) {} }, 400);   // Zapiet loads after us and redraws as the customer clicks; the theme re-renders the recap
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', zapietWatch);
  else zapietWatch();

  // Reconcile after any cart mutation (drawer add/remove/qty change).
  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function () {
      var url = arguments[0];
      var u = (typeof url === 'string') ? url : (url && url.url) || '';
      var p = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(u)) {
        p.then(function () { setTimeout(function () { reconcile(); }, 250); }).catch(function () {});
      }
      return p;
    };
  }

  // If we reloaded the cart page right after re-adding the charge, show the notice now.
  function showPendingNotice() {
    try {
      var kind = sessionStorage.getItem('aaCartGuardNote');
      if (kind) { sessionStorage.removeItem('aaCartGuardNote'); notify(msgFor(kind)); }
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { showPendingNotice(); reconcile(); });
  else { showPendingNotice(); reconcile(); }

  // "Empty cart" button (added to the cart page template).
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('.aa-empty-cart') : null;
    if (!el) return;
    e.preventDefault();
    if (!window.confirm('Remove all items from your cart?')) return;
    el.disabled = true;
    fetch('/cart/clear.js', { method: 'POST', headers: { 'Accept': 'application/json' } })
      .then(function () { location.href = '/cart'; })
      .catch(function () { el.disabled = false; });
  });

  window.AACartGuard = { reconcile: reconcile, _tally: tally, _setupFeeSteps: setupFeeSteps,
    // v3.7 - the coater's minimum, for custom.js, the bar tables and the made-to-order box
    PC_FREE_M2: PC_FREE_M2, pcSetupWaived: pcSetupWaived, pcAreaM2: pcAreaM2, pcLengthM: pcLengthM,
    pcPerimeterM: pcPerimeterM, pcFreeFromLengths: pcFreeFromLengths, _pcColourAreaM2: pcColourAreaM2 };
})();
